type EventDates = {
  start_date: string;
  end_date?: string | null;
  event_timezone?: string | null;
  automation_key?: string | null;
};

const isMidnightUtc = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) ||
  /^\d{4}-\d{2}-\d{2}T00:00:00(?:\.0+)?(?:Z|\+00:00)$/.test(value);

// The dashboard's calendar picker stores dates at midnight UTC. Those older
// entries do not establish a local start time. Automated entries have verified
// source clocks, including events that really start at midnight.
export function eventHasExactTimes(event: EventDates): boolean {
  return Boolean(event.automation_key) ||
    ![event.start_date, event.end_date || event.start_date].every(isMidnightUtc);
}

export function eventDisplayTimeZone(event: EventDates): string {
  if (!eventHasExactTimes(event)) return "UTC";
  if (event.event_timezone) {
    try {
      new Intl.DateTimeFormat("en", { timeZone: event.event_timezone });
      return event.event_timezone;
    } catch {
      // Keep server and browser output consistent when a timezone is unknown.
    }
  }
  return "UTC";
}

export function formatEventDate(
  event: EventDates,
  value: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: eventDisplayTimeZone(event),
  }).format(date);
}

export function formatEventDateRange(event: EventDates): string {
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric", month: "short", year: "numeric",
  };
  const start = formatEventDate(event, event.start_date, "en-GB", options);
  const end = event.end_date
    ? formatEventDate(event, event.end_date, "en-GB", options)
    : "";
  return end && end !== start ? `${start} - ${end}` : start;
}

export function formatEventTimeRange(event: EventDates): string {
  if (!eventHasExactTimes(event)) return "";
  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  };
  const start = formatEventDate(event, event.start_date, "en-GB", options);
  if (!start) return "";
  const end = event.end_date
    ? formatEventDate(event, event.end_date, "en-GB", options)
    : "";
  const dateOptions: Intl.DateTimeFormatOptions = {
    day: "numeric", month: "short", year: "numeric",
  };
  const startDay = formatEventDate(event, event.start_date, "en-GB", dateOptions);
  const endDay = event.end_date
    ? formatEventDate(event, event.end_date, "en-GB", dateOptions)
    : "";
  const endLabel = endDay && endDay !== startDay ? `${end} (${endDay})` : end;
  const zoneLabel = eventDisplayTimeZone(event) === "UTC" ? "UTC" : "local time";
  return `${start}${endLabel ? ` - ${endLabel}` : ""} ${zoneLabel}`;
}

export function eventSchemaDate(event: EventDates, value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  return eventHasExactTimes(event) ? value : value.slice(0, 10);
}
